
import { namespace, Component, Watch, Vue } from 'nuxt-property-decorator';
import {
  FormEntryDto,
  EventDto,
  FormDto,
  PageType,
  PageScope,
  getSubmissionTitle,
  getFirstName,
  getLastName,
  PageDto,
  FilteringType,
  FormType,
  FormFieldType,
  getFieldById,
  castQuery,
  isSuccess,
  GetFormEntriesModel,
  LayoutModes,
  ListingOptions,
  OrderingQueryFilters,
  ScheduleDto,
  SubmissionScope,
  StatusFilter,
  buildFilterChoicesDictionary,
  castQueryArrays,
} from 'fourwaves-shared';
import { NuxtError } from '@nuxt/types';
import {
  PresentationFilterBar,
  PresentationCard,
  PresentationElement,
  FilterPagination,
  NoResults,
} from 'fourwaves-shared/components';
import { debounce, isEqual } from 'lodash-es';
import Header from '@/components/layout/Header.vue';

const EventsModule = namespace('events');

@Component({
  scrollToTop: true,
  components: {
    Header,
    PresentationCard,
    NoResults,
    FilterPagination,
    PresentationFilterBar,
  },
  validate({ store }) {
    const page = store.state.events.pages.find(({ type }: PageDto) => type === PageType.Abstracts);
    if (page && page.scope === PageScope.Participants) {
      if (!store.$auth.loggedIn) {
        const error: NuxtError = new Error();
        error.statusCode = 401;
        throw error;
      } else if (!store.getters['events/isAllowedToVirtualCall']) {
        const error: NuxtError = new Error();
        error.statusCode = 403;
        throw error;
      }
    }
    return !!page;
  },
})
export default class AbstractsIndexPage extends Vue {
  @EventsModule.State sessions!: ScheduleDto[];
  @EventsModule.State pages!: PageDto[];
  @EventsModule.State currentEvent!: EventDto;
  @EventsModule.Getter isParticipant!: boolean;
  @EventsModule.Getter isOrganizer!: boolean;
  @EventsModule.Getter isReviewer!: boolean;
  @EventsModule.State isReviewingFeatureEnabled!: boolean;
  @EventsModule.Getter isVpsOpen!: boolean;

  formId = '';
  form: FormDto | null = null;
  page: PageDto | null = null;
  presentations: PresentationElement[] = [];
  isReviewFilteringAvailable = false;
  isLoading = false;
  firstLoadCompleted = false;
  totalCount = 0;
  totalPages = 1;
  submissionScope: SubmissionScope | null = null;

  readonly LayoutModes = LayoutModes;

  public head() {
    return { title: this.page ? this.transl8(this.page.name) : '' };
  }

  get headerTitle(): string | null {
    if (!this.page) return null;

    return this.transl8(this.page.name);
  }

  get model(): GetFormEntriesModel {
    const {
      page = 1,
      searchTerm = '',
      sessionIds = [],
      isPresentingNow = false,
      isBookmarked = false,
      reviewStatusFilters = [],
    } = castQuery<Partial<GetFormEntriesModel>>(this.$route.query, {
      numbers: ['page'],
      booleans: ['isPresentingNow', 'isBookmarked'],
      arrays: ['sessionIds', 'submissionDecisionFilter'],
    });
    const { filterChoices = [] } = castQueryArrays<{ filterChoices: string[] }>(this.$route.query, ['filterChoices']);

    const formFields = this.form?.formSections.flatMap(x => x.formFields) || [];
    const filterChoicesDict = buildFilterChoicesDictionary(filterChoices, formFields);

    return {
      ...this.orderingQueryFilters,
      page,
      searchTerm,
      filterChoices: filterChoicesDict,
      sessionIds,
      isPresentingNow,
      isBookmarked,
      reviewStatusFilters,
      formId: this.formId,
      pageSize: 18,
      isPublishedFilter: true,
      filterCurrentUserReviews: true,
      filteringType: FilteringType.ContainsAll,
      submissionScope: this.submissionScope,
      submissionDecisionsFilter: [],
      submissionDecisionsFilteringType: FilteringType.ContainsAny,
      statusFilters: [StatusFilter.Submitted, StatusFilter.Scheduled],
    };
  }

  get orderingQueryFilters(): { sortField: string; sortDirection: 'ascending' | 'descending' } {
    if (!this.form) return { sortField: OrderingQueryFilters.SequentialId, sortDirection: 'ascending' };
    const { listingOption = ListingOptions.Numerical } = this.$route.query;
    if (listingOption === ListingOptions.Upvotes)
      return { sortField: OrderingQueryFilters.UpvoteCount, sortDirection: 'descending' };

    const queryValue =
      listingOption === ListingOptions.Alphabetical
        ? getFieldById(this.form, this.form.submissionTitleFormFieldId!)!.id
        : OrderingQueryFilters.SequentialId;

    return { sortField: queryValue, sortDirection: 'ascending' };
  }

  get layoutMode(): LayoutModes {
    const queryLayout = this.$device.laptop && (this.$route.query.layout as LayoutModes | undefined);
    return queryLayout || LayoutModes.grid;
  }

  @Watch('model', { deep: true })
  async onModelChanged(newModel: GetFormEntriesModel, oldModel: GetFormEntriesModel) {
    if (isEqual(newModel, oldModel)) return;
    this.isLoading = true;
    await this.fetchData();
    if (this.firstLoadCompleted) this.$scrollTo('body');
  }

  async created() {
    this.fetchData = debounce(this.fetchData, 250) as () => Promise<void>;
    this.form = this.currentEvent.forms.find(({ type }) => type === FormType.Submission)!;
    this.formId = this.form.id;
    this.page = this.pages.find(({ type }) => type === PageType.Abstracts) || null;
    this.submissionScope = this.page?.submissionScope || null;

    if (this.isReviewer && this.isReviewingFeatureEnabled) {
      const { totalCount = 0 } = await this.$api.getReviews({
        eventId: this.currentEvent.id,
        userId: this.$auth.user.id,
        status: null,
        page: 1,
        pageSize: 0,
      });

      this.isReviewFilteringAvailable = totalCount > 0;
    }
  }

  public async fetchData() {
    if (!this.formId) {
      this.isLoading = false;
      return;
    }

    const { items, totalCount, totalPages } = await this.$api.getFormEntries(this.model);
    this.presentations = items.map(this.buildPresentationElement);

    this.totalCount = totalCount;
    this.totalPages = totalPages;
    this.isLoading = false;
    if (!this.firstLoadCompleted) this.firstLoadCompleted = true;
  }

  public buildPresentationElement(presentation: FormEntryDto): PresentationElement {
    const submitterFirstName = getFirstName(presentation, this.form!);
    const submitterLastName = getLastName(presentation, this.form!);

    return {
      ...presentation,
      submitterFirstName,
      submitterLastName,
      title: getSubmissionTitle(presentation, this.form!),
      connectionCount: presentation.videoParticipantCount,
      thumbnail: presentation.thumbnail ? `${presentation.thumbnail}?fit=max&w=800` : undefined,
      hasVideo: presentation.formEntryValues.some(({ fieldType }) => fieldType === FormFieldType.Video),
    };
  }

  public async toggleUpvote({ id, isUpvotedByCurrentUser, upvoteCount }: PresentationElement) {
    const canUserVote = this.isParticipant || this.isOrganizer;
    const presentation = this.presentations.find(p => p.id === id);
    if (!presentation || !canUserVote) return;

    const updateResult = isUpvotedByCurrentUser
      ? await this.$api.deleteFormEntryUpvote(id)
      : await this.$api.upvoteFormEntry(id);

    if (isSuccess(updateResult)) {
      presentation.upvoteCount = isUpvotedByCurrentUser ? upvoteCount - 1 : upvoteCount + 1;
      presentation.isUpvotedByCurrentUser = !isUpvotedByCurrentUser;
    }
  }

  public async toggleBookmark({ id, isBookmarkedByCurrentUser }: PresentationElement) {
    const presentation = this.presentations.find(p => p.id === id);
    if (!presentation || !this.$auth.loggedIn) return;

    const updateResult = isBookmarkedByCurrentUser
      ? await this.$api.unbookmarkFormEntry(id)
      : await this.$api.bookmarkFormEntry(id);

    if (isSuccess(updateResult)) presentation.isBookmarkedByCurrentUser = !isBookmarkedByCurrentUser;
  }
}
